import moment from 'moment';
import {
  MapReportedProjectType,
  MapAssignmentType,
  ReportedHoursType,
  MapReportedData,
  WeeklyChartDataType,
  ProjectChartDataType,
} from './types';

export const isInRange = (from: string, to: string, weekStart: moment.Moment, weekEnd: moment.Moment) => {
  return weekEnd.isSameOrAfter(moment(from)) && weekStart.isSameOrBefore(moment(to));
};

export const createEmptyProject = (name: string) => ({
  name,
  value: null,
  minPercent: null,
  maxPercent: null,
});

export const mergeProjectData = (project: MapReportedProjectType, update: Partial<typeof project>) => {
  return { ...project, ...update };
};

export const generateWeeklyRawData = (
  assignments: MapAssignmentType[],
  reportedWeeks: ReportedHoursType[],
): MapReportedData[] => {
  return reportedWeeks.map(week => {
    const { startDate, endDate, reportedHours } = week;
    const weekLabel = `${moment(startDate).format('MMM DD')} - ${moment(endDate).format('MMM DD')}`;
    const label = moment(startDate).format('MMM');
    const weekStart = moment(startDate);
    const weekEnd = moment(endDate);

    const projectMap = new Map<string, MapReportedProjectType>();

    // Step 1: fill value from reportedHours
    reportedHours.forEach((reported: any) => {
      const name = reported.projectName;
      const value = reported.actualHoursPercent >= 0 ? Number((reported.actualHoursPercent || 0).toFixed(2)) : null;
      projectMap.set(name, createEmptyProject(name));
      projectMap.get(name)!.value = value || null;
    });

    // Step 2: fill min/max from assignments
    assignments.forEach((assignment: any) => {
      const { name } = assignment.project;

      if (isInRange(assignment.from, assignment.to, weekStart, weekEnd)) {
        if (!projectMap.has(name)) {
          projectMap.set(name, createEmptyProject(name));
        }

        const project = projectMap.get(name);
        projectMap.set(
          name,
          mergeProjectData(project!, {
            minPercent: assignment.minPercent,
            maxPercent: assignment.maxPercent,
          }),
        );
      }
    });

    return {
      label,
      startDate,
      endDate,
      weekRange: weekLabel,
      projects: Array.from(projectMap.values()),
    };
  });
};

export const generateWeeklyDataFromRaw = (rawData: MapReportedData[]): WeeklyChartDataType[] => {
  return rawData.map((week, index) => {
    const name = index === 0 || rawData[index - 1].label !== week.label ? week.label : '';
    const entry: any = {
      name,
      pyloadData: week.weekRange,
    };

    week.projects.forEach((project: any) => {
      entry[project.name] = project.value;
    });

    return entry;
  });
};

export const generateProjectDataFromRaw = (rawData: MapReportedData[]): ProjectChartDataType[] => {
  const projectNames = Array.from(new Set(rawData.flatMap(week => week.projects.map((p: any) => p.name))));

  return projectNames.map(name => {
    let previousMonth: string | null = null;

    const data = rawData.map(week => {
      const showLabel = previousMonth !== week.label;
      previousMonth = week.label;

      const projectData = week.projects.find((p: any) => p.name === name);

      return {
        name: showLabel ? week.label : '',
        week: week.weekRange,
        rangeMin: projectData?.minPercent ?? null,
        rangeDelta:
          projectData?.minPercent != null && projectData?.maxPercent != null
            ? projectData.maxPercent - projectData.minPercent
            : null,
        reportedTime: projectData?.value ?? null,
      };
    });

    return {
      name,
      data,
    };
  });
};
